// ── #192/#212/#509 row-filter evidence gating ────────────────────
//
// The row predicates themselves live in GameTable (`hiddenEmptySports` +
// `activityReady`; #212 generalized #192's tennis-only boolean to a per-sport
// suppression list, and #509 added a board-wide Kalshi-markets-only filter).
// This module owns the two decisions FEEDING them, pulled out of ValueDashboard
// so they can be tested without rendering the whole board:
//
//   1. is the filter active at all            → polyActivityPollingEnabled
//   2. is an EMPTY activity map trustworthy   → activityEvidenceIsAuthoritative
//
// The governing rule for (2) is asymmetric and deliberately so: POSITIVE
// evidence (a position, an open order) is always safe to act on, but the
// ABSENCE of evidence only means "no exposure" if every venue read actually
// answered. A read that failed, is still in flight, or is structurally unable
// to answer must leave the row visible — hiding a matchup the user holds money
// on is the one outcome this filter may never produce.

import type { PolyAccountStatus } from '@/stores/polyAccountStore';
import type { PolyRelayOrdersStatus } from '@/hooks/usePolymarketRelayOrders';
import type { PolyRelayPositionsStatus } from '@/hooks/usePolymarketRelayPositions';
import type { Sport } from './types';

export interface PolyActivityPollingInput {
  /** The board the filter is scoped to (#212 keeps it moneyline-only). */
  isMoneylineBoard: boolean;
  /** Sports currently on the moneyline board (multi-outcome already excluded). */
  selectedMoneylineSports: readonly Sport[];
  /** The user's persisted per-sport suppression list (empty = filter off). */
  hiddenEmptySports: readonly Sport[];
  /** #509 — the Kalshi-markets-only opt-in. A SECOND consumer of the same
   *  exposure evidence, and unlike the suppression list it is not per-sport:
   *  when it is on, every row on the board can be hidden, so the reads are
   *  needed regardless of which sports are selected. */
  kalshiMarketsOnly: boolean;
  /** #654 — the minimized-game lens: a THIRD consumer of the same evidence.
   *  Like #509 it is board-wide rather than per-sport, but unlike either
   *  neighbour it is not a setting the user leaves configured — it is a set of
   *  per-game marks. Pass true only when the lens applies to the current board
   *  AND at least one game is actually marked, so a CFB board with an empty
   *  mark list leaves no timer running. */
  cfbMinimizedActive: boolean;
}

/** The Polymarket position/order reads exist only to answer "does the user have
 *  exposure on this otherwise-hidden row". A filter that cannot affect the
 *  current board is not a reason to poll: revealing rows is the cheap state, so
 *  a board with no active row filter must leave no timer running against the
 *  org's read budget.
 *
 *  THREE filters now depend on this evidence and ANY one arms the reads:
 *
 *  - #212 empty-row suppression, which is per-sport. Only SELECTED sports are
 *    eligible — a hidden-but-unselected sport keeps the reads off.
 *  - #509 Kalshi-markets-only, which is board-wide. No sport predicate applies.
 *  - #654 the minimized lens, board-wide, and armed only while at least one
 *    game is actually marked.
 *
 *  This coupling is load-bearing and easy to miss: both poly hooks start at
 *  `status: 'idle'` and guard their only fetch effect with `if (!enabled)
 *  return`, so a filter that consumes `activityEvidenceIsAuthoritative` without
 *  arming the reads here can never see a 'ready' status. It would not fail
 *  loudly — it would silently hide nothing at all. */
export function polyActivityPollingEnabled({
  isMoneylineBoard,
  selectedMoneylineSports,
  hiddenEmptySports,
  kalshiMarketsOnly,
  cfbMinimizedActive,
}: PolyActivityPollingInput): boolean {
  if (!isMoneylineBoard) return false;
  if (kalshiMarketsOnly || cfbMinimizedActive) return true;
  return selectedMoneylineSports.some((sport) => hiddenEmptySports.includes(sport));
}

export interface ActivityEvidenceInput {
  /** accountStore connection status (Kalshi positions + open orders). */
  accountStatus: string;
  /** useUserKalshiPOS in-flight. */
  kalshiActivityLoading: boolean;
  /** useUserKalshiPOS error, or null. */
  kalshiActivityError: string | null;
  polyPositionsStatus: PolyRelayPositionsStatus;
  polyOrdersStatus: PolyRelayOrdersStatus;
  personalPolyStatus: PolyAccountStatus;
}

/** True only when EVERY activity source has authoritatively answered, so an
 *  empty activity map can be read as "the user holds nothing here".
 *
 *  Per-source reasoning:
 *
 *  - Kalshi needs a connected account store AND a settled POS read; a loading
 *    or errored POS response carries no position information.
 *  - The poly relay hooks must be exactly 'ready'. `unavailable` is NOT an
 *    answer — it is the relay reporting NOT_IN_ORG / PERSONAL_KEY_MODE /
 *    NO_POLY_CRED / ONLY_LEGACY_EOA_CRED, i.e. "I cannot tell you whether this
 *    trader has a position" — and both hooks pair that code with an empty
 *    array, which previously read as negative evidence. 'idle', 'loading' and
 *    'error' are likewise non-answers. (This also covers the legacy-EOA case
 *    with no separate arm: ONLY_LEGACY_EOA_CRED arrives as `unavailable`.)
 *  - Personal-key Polymarket is a PARTIAL surface by construction:
 *    polyAccountStore carries openOrders and no positions at all, so a trader
 *    running their own key can hold a position no source here can see.
 *    'disconnected' is therefore the only safe personal state — no personal key
 *    in play means nothing is unobservable. 'connected' fails open because the
 *    position half is permanently missing; 'connecting' and 'error' fail open
 *    as in-flight / unreadable. */
export function activityEvidenceIsAuthoritative({
  accountStatus,
  kalshiActivityLoading,
  kalshiActivityError,
  polyPositionsStatus,
  polyOrdersStatus,
  personalPolyStatus,
}: ActivityEvidenceInput): boolean {
  return (
    accountStatus === 'connected' &&
    !kalshiActivityLoading &&
    kalshiActivityError === null &&
    polyPositionsStatus === 'ready' &&
    polyOrdersStatus === 'ready' &&
    personalPolyStatus === 'disconnected'
  );
}
